import { ShipmentCustomerActions } from "./components/shipment-customer-actions";
import { CustomerShipmentDetails } from "./customer-shipment-details";
import { useState } from "react";
import { useParams, Link } from "react-router";
import { Button, Card, CardBody, Chip, Skeleton } from "@heroui/react";
import { ArrowLeft, Package } from "lucide-react";
import { Button as DialogButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCustomerOrdersV2,
  getOrderStatus,
} from "@/api/use-customer-orders-v2";
import { ShipmentStatusBadge } from "@/components/shipment-status-badge";
import { useTranslation } from "react-i18next";
import { useOrderPayments } from "@/api/use-order-payments";
import { useRequestCancellation } from "@/api/use-request-cancellation";
import { toast } from "@/hooks/use-toast";
import { PayOrderDialog } from "./pay-order-dialog";
import { CancellationPendingNotice } from "./cancellation-pending-notice";
import { canRequestCustomerShipmentCancellation } from "@/api/shipment-action-eligibility";
import {
  getReturnAmountDue,
  ReturnRefundBreakdown,
} from "./return-refund-breakdown";

import {
  OrderPaymentSummary,
  OrderPriceDetails,
} from "./order-payment-summary";

export default function OrderDetailsScreen() {
  const { t, i18n } = useTranslation("app");
  const { orderId } = useParams<{ orderId: string }>();
  const [returnTransportToPay, setReturnTransportToPay] = useState<
    string | null
  >(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const { data: response, isLoading } = useCustomerOrdersV2({
    page: 1,
    limit: 100,
  });
  const { data: payments = [] } = useOrderPayments(orderId);
  const requestCancellation = useRequestCancellation({
    onSuccess: (data) => {
      if (!data.success) {
        toast({
          variant: "destructive",
          title: t("orders.toastCancellationError"),
          description: data.message,
        });
        return;
      }
      toast({
        title: t("orders.toastCancellationTitle"),
        description: data.message,
      });
      setCancelDialogOpen(false);
    },
    onError: () =>
      toast({
        variant: "destructive",
        title: t("orders.toastCancellationError"),
      }),
  });

  const order = response?.data?.find((o) => o.id === orderId);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-8 w-32 rounded-medium" />
        <Card shadow="none" className="border border-divider">
          <CardBody className="p-6 space-y-4">
            <Skeleton className="h-6 w-48 rounded-medium" />
            <Skeleton className="h-4 w-full rounded-medium" />
            <Skeleton className="h-4 w-3/4 rounded-medium" />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card shadow="none" className="border border-divider">
          <CardBody className="py-12 text-center">
            <Package className="h-12 w-12 text-default-300 mx-auto mb-3" />
            <p className="font-medium text-default-700">
              {t("orderDetails.notFoundTitle")}
            </p>
            <p className="text-small text-default-500 mt-1">
              {t("orderDetails.notFoundBody")}
            </p>
            <Button
              as={Link}
              to="/orders"
              variant="bordered"
              size="sm"
              startContent={<ArrowLeft className="h-4 w-4" />}
              className="mt-4"
            >
              {t("orderDetails.backToOrders")}
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const status = getOrderStatus(order);
  const shipments = order.shipments ?? [];
  const shipment = shipments[0];
  const hasPendingCancellation = order.shipments?.some(
    (item) => item.cancellationRequestedAt
  );
  const cancellationShipments =
    order.shipments?.filter((item) =>
      canRequestCustomerShipmentCancellation(item.status)
    ) ?? [];
  const orderReference = `ORD-${order.orderNumber}`;
  const showCancel =
    !hasPendingCancellation && cancellationShipments.length > 0;
  const shipmentReturns =
    order.shipments?.flatMap((item) =>
      (item.returnTransports ?? []).map((transport) => ({
        transport,
        shipmentReference: item.shipmentNumber ?? item.id,
      }))
    ) ?? [];
  const returnTransports = shipmentReturns.map((item) => item.transport);
  const selectedReturnTransport = returnTransports.find(
    (transport) => transport.id === returnTransportToPay
  );
  const amountDueToPay = selectedReturnTransport
    ? getReturnAmountDue(selectedReturnTransport, payments)
    : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <Link
        to="/orders"
        className="inline-flex items-center gap-1 text-small text-default-500 hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("orderDetails.backToOrders")}
      </Link>

      {hasPendingCancellation && <CancellationPendingNotice />}

      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-start gap-3 rounded-xl border border-default-200 bg-default-50/40 p-4">
        <div className="min-w-0 space-y-1.5">
          <h1 className="text-xl font-bold tracking-tight">
            {shipments.length > 1
              ? orderReference
              : `SHP-${shipment?.shipmentNumber ?? "…"}`}
          </h1>
          <p className="text-xs text-default-500">
            {new Date(order.createdAt).toLocaleDateString(
              i18n.language === "zh" ? "zh-CN" : "en-CA",
              {
                month: "short",
                day: "numeric",
                year: "numeric",
              }
            )}
          </p>
        </div>
        <div className="min-w-0 space-y-3">
          <OrderPaymentSummary order={order} variant="detail" />
        </div>
        <div className="col-span-2 flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {shipments.length === 1 ? (
              <ShipmentStatusBadge
                status={status}
                paymentStatus={order.paymentStatus}
                className="px-4! py-1.5! text-sm!"
              />
            ) : (
              <Chip variant="flat" size="md">
                {t("orderDetails.shipmentsCount", { count: shipments.length })}
              </Chip>
            )}
          </div>
          <OrderPriceDetails order={order} />
        </div>
        <div className="col-span-2 flex min-w-0 flex-wrap items-center gap-2 border-t border-default-200 pt-3">
          {shipments.length === 1 && shipment && (
            <ShipmentCustomerActions
              shipment={shipment}
              disabled={Boolean(hasPendingCancellation)}
            />
          )}
          {showCancel && (
            <div className="min-w-0 sm:ml-auto">
              <Button
                size="sm"
                color="danger"
                variant="bordered"
                className="h-auto min-h-8 max-w-full whitespace-normal"
                aria-label={t("orderDetails.requestOrderCancellation", {
                  orderReference,
                })}
                onPress={() => setCancelDialogOpen(true)}
                aria-describedby="order-cancellation-scope"
              >
                {t("orders.requestCancellation")}
              </Button>
              <p id="order-cancellation-scope" className="sr-only">
                {t("orderDetails.cancellationScope", {
                  count: cancellationShipments.length,
                })}
              </p>
            </div>
          )}
        </div>
      </div>

      {shipmentReturns.map(({ transport, shipmentReference }) => (
        <ReturnRefundBreakdown
          key={transport.id}
          transport={transport}
          shipmentReference={shipmentReference}
          payments={payments}
          onPayAmountDue={() => setReturnTransportToPay(transport.id)}
        />
      ))}

      {order.shipments?.map((item) => (
        <CustomerShipmentDetails
          key={item.id}
          shipment={item}
          showHeading={shipments.length > 1}
          actions={
            shipments.length > 1 ? (
              <ShipmentCustomerActions
                shipment={item}
                disabled={Boolean(hasPendingCancellation)}
              />
            ) : undefined
          }
        />
      ))}
      {amountDueToPay > 0 && (
        <PayOrderDialog
          open
          onOpenChange={(open) => !open && setReturnTransportToPay(null)}
          orderId={order.id}
          totalCents={amountDueToPay}
          orderNumber={`ORD-${order.orderNumber}`}
          allowInvoice={false}
          returnTransport={selectedReturnTransport}
          purpose="RETURN"
        />
      )}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("orderDetails.cancellationTitle", { orderReference })}
            </DialogTitle>
            <DialogDescription>
              {t("orderDetails.cancellationBody", { orderReference })}
            </DialogDescription>
            <ul className="list-disc pl-5 text-sm">
              {cancellationShipments.map((item) => (
                <li key={item.id}>SHP-{item.shipmentNumber ?? "…"}</li>
              ))}
            </ul>
          </DialogHeader>
          <DialogFooter>
            <DialogButton
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
            >
              {t("orders.keepOrder")}
            </DialogButton>
            <DialogButton
              variant="destructive"
              onClick={() => requestCancellation.mutate({ orderId: order.id })}
              disabled={requestCancellation.isPending}
              aria-busy={requestCancellation.isPending}
            >
              {requestCancellation.isPending
                ? t("orders.submitting")
                : t("orders.requestCancellation")}
            </DialogButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
